import { useState, useEffect } from 'react';
import { useAiTasks, AiTask } from '@/contexts/AiTasksContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sparkles, Loader2, CheckCircle2, XCircle, X, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const TASK_TYPE_LABELS: Record<string, string> = {
  'product-memo': 'Mémo produit',
  'product-info': 'Info produit',
  'cee-memo': 'Mémo CEE',
  'cee-analysis': 'Analyse CEE',
  'cheat-sheet': 'Cheat Sheet',
  'training': 'Formation',
  'mailing': 'Templates email',
  'script': 'Script commercial',
  'eligibility-config': 'Config éligibilité',
};

function TaskItem({ task, onRemove, queuePosition }: { task: AiTask; onRemove: (id: string) => void; queuePosition?: number }) {
  const [elapsed, setElapsed] = useState('');

  useEffect(() => {
    if (task.status === 'pending' && task.startedAt) {
      const interval = setInterval(() => {
        const seconds = Math.floor((Date.now() - task.startedAt!.getTime()) / 1000);
        setElapsed(`${seconds}s`);
      }, 1000);
      return () => clearInterval(interval);
    } else if (task.status === 'queued') {
      setElapsed('En attente');
    } else if (task.startedAt) {
      setElapsed(formatDistanceToNow(task.startedAt, { addSuffix: false, locale: fr }));
    }
  }, [task.startedAt, task.status]);

  return (
    <div className={cn(
      "flex items-center gap-3 p-3 rounded-lg transition-colors",
      task.status === 'queued' && "bg-muted/50",
      task.status === 'pending' && "bg-primary/5",
      task.status === 'success' && "bg-green-500/10",
      task.status === 'error' && "bg-destructive/10"
    )}>
      <div className="shrink-0">
        {task.status === 'queued' && (
          <Clock className="h-4 w-4 text-muted-foreground" />
        )}
        {task.status === 'pending' && (
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        )}
        {task.status === 'success' && (
          <CheckCircle2 className="h-4 w-4 text-green-600" />
        )}
        {task.status === 'error' && (
          <XCircle className="h-4 w-4 text-destructive" />
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{task.label}</p>
        <p className="text-xs text-muted-foreground">
          {TASK_TYPE_LABELS[task.type] || task.type}
          {task.error && <span className="text-destructive ml-1">• {task.error}</span>}
        </p>
      </div>
      
      <div className="flex items-center gap-2 shrink-0">
        <span className={cn(
          "text-xs tabular-nums",
          task.status === 'pending' && "text-primary font-medium",
          task.status === 'queued' && "text-muted-foreground",
          task.status !== 'pending' && task.status !== 'queued' && "text-muted-foreground"
        )}>
          {task.status === 'queued' && queuePosition !== undefined ? (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {queuePosition + 1}e
            </span>
          ) : elapsed}
        </span>
        
        {(task.status === 'success' || task.status === 'error') && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onRemove(task.id)}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function AiTasksIndicator() {
  const { tasks, removeTask, hasActiveTasks, queuedCount, pendingCount } = useAiTasks();
  const [open, setOpen] = useState(false);

  const recentTasks = tasks.slice(-10).reverse(); // Show last 10, newest first
  
  // Calculate queue positions for queued tasks
  const queuedTasks = tasks.filter(t => t.status === 'queued');
  const getQueuePosition = (taskId: string) => {
    return queuedTasks.findIndex(t => t.id === taskId);
  };

  // Don't render if no tasks
  if (tasks.length === 0) {
    return null;
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "relative",
            hasActiveTasks && "text-primary"
          )}
        >
          {hasActiveTasks ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Sparkles className="h-5 w-5" />
          )}
          
          {(pendingCount > 0 || queuedCount > 0) && (
            <Badge
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-[10px] bg-primary"
            >
              {pendingCount + queuedCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-80 p-2">
        <div className="flex items-center justify-between px-2 pb-2 border-b mb-2">
          <h4 className="font-semibold text-sm">Tâches IA</h4>
          <div className="flex gap-1">
            {pendingCount > 0 && (
              <Badge variant="default" className="text-xs">
                {pendingCount} en cours
              </Badge>
            )}
            {queuedCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {queuedCount} en attente
              </Badge>
            )}
          </div>
        </div>
        
        <div className="max-h-80 overflow-y-auto space-y-1">
          {recentTasks.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Aucune tâche IA récente
            </p>
          ) : (
            recentTasks.map(task => (
              <TaskItem 
                key={task.id} 
                task={task} 
                onRemove={removeTask}
                queuePosition={task.status === 'queued' ? getQueuePosition(task.id) : undefined}
              />
            ))
          )}
        </div>
        
        {tasks.length > 10 && (
          <p className="text-xs text-muted-foreground text-center pt-2 border-t mt-2">
            +{tasks.length - 10} autres tâches
          </p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
